/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Combobox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Divider,
    Dropdown,
    Field,
    makeStyles,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Option,
    shorthands,
    Tab,
    TabList,
    tokens,
    type SelectTabData,
    type SelectTabEvent,
} from '@fluentui/react-components';
import { DeleteRegular, PersonAddRegular } from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import type { InfoGroupItem } from '../../models/InfoGroupItem';
import type { ProjektmitarbeiterItem } from '../../models/ProjektmitarbeiterItem';
import { DataService } from '../../services/dataService';

const useStyles = makeStyles({
    container: {
        padding: `0 ${tokens.spacingHorizontalL}`,
        height: '100%',
        overflow: 'auto',
    },
    overviewSection: {
        marginTop: tokens.spacingVerticalL,
        marginBottom: tokens.spacingVerticalXXL,
    },
    tabContent: {
        marginTop: tokens.spacingVerticalL,
    },
    formSection: {
        display: 'flex',
        alignItems: 'flex-end',
        ...shorthands.gap(tokens.spacingHorizontalM),
    },
    inputField: {
        minWidth: '250px',
    },
    dropdown: {
        minWidth: '250px',
    },
    memberCard: {
        padding: tokens.spacingVerticalM,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        marginBottom: tokens.spacingVerticalS,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    memberName: {
        fontWeight: 600,
        fontSize: '1rem',
    },
    deleteButton: {
        marginLeft: '10px',
        transition: 'all 0.3s ease',
        ':hover': {
            backgroundColor: 'firebrick',
            color: 'white',
            transform: 'scale(1.05)',
        },
    },
    combobox: {
        minWidth: '300px',
    },
});

const AVAILABLE_GROUPS = [
    { value: '1', text: 'Info_CO' },
    { value: '2', text: 'Info_Ful' },
];

const InformationGroupsTab: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const accountInfo = accounts[0] || null;
    const dataService = React.useMemo(() => {
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accountInfo]);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMitarbeiter, setSelectedMitarbeiter] = useState<ProjektmitarbeiterItem | null>(null);
    const [filteredMitarbeiter, setFilteredMitarbeiter] = useState<ProjektmitarbeiterItem[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<string>('');
    const [selectedTab, setSelectedTab] = useState<string>('1');
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [infoGroups, setInfoGroups] = useState<InfoGroupItem[]>([]);
    const [actionItem, setActionItem] = useState<InfoGroupItem | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Fetch info groups on component load
    useEffect(() => {
        const fetchInfoGroups = async () => {
            if (!dataService) return;
            try {
                const groups = await dataService.getInfoGroups();
                setInfoGroups(groups);
                if (groups.length > 0) {
                    setSelectedTab('1');
                }
            } catch (error) {
                console.error('Error fetching info groups:', error);
            }
        };
        fetchInfoGroups();
    }, [dataService]);

    const getMembersByGroup = (groupId: string): InfoGroupItem[] => {
        return infoGroups.filter((item) => item.groupId.toString() === groupId);
    };

    const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
        const value = data.value as string;
        setSelectedTab(value);
    };

    const handleMitarbeiterSearch = async (event: any) => {
        if (!dataService) return;
        const value = event.target.value;
        setSearchQuery(value);
        if (value.length >= 2) {
            try {
                const mitarbeiter = await dataService.getMitarbeiter();
                const filtered = mitarbeiter.filter((m) => {
                    const benutzername = m.benutzername || '';
                    const fullName = m.fullName || `${m.vorname} ${m.nachname}`;
                    return (
                        benutzername.toLowerCase().includes(value.toLowerCase()) ||
                        fullName.toLowerCase().includes(value.toLowerCase())
                    );
                });
                setFilteredMitarbeiter(filtered);
            } catch (error) {
                console.error('Error searching mitarbeiter:', error);
            }
        } else {
            setFilteredMitarbeiter([]);
        }
    };

    const handleMitarbeiterSelect = (_event: any, data: any) => {
        const mitarbeiter = filteredMitarbeiter.find((m) => m.personalID === parseInt(data.optionValue));
        if (mitarbeiter) {
            setSelectedMitarbeiter(mitarbeiter);
            const displayName = mitarbeiter.fullName || `${mitarbeiter.vorname} ${mitarbeiter.nachname}`;
            setSearchQuery(displayName);
        }
    };

    const handleAddToGroup = async () => {
        if (!dataService || !selectedMitarbeiter || !selectedGroup) return;

        const displayName = selectedMitarbeiter.fullName || `${selectedMitarbeiter.vorname} ${selectedMitarbeiter.nachname}`;
        const newItem: InfoGroupItem = {
            id: 0,
            userId: selectedMitarbeiter.personalID,
            userName: displayName,
            groupId: parseInt(selectedGroup),
            groupName: AVAILABLE_GROUPS.find((g) => g.value === selectedGroup)?.text || '',
        };

        const updatedGroups = [...infoGroups, newItem];
        setInfoGroups(updatedGroups);

        // Reset form
        setSearchQuery('');
        setSelectedMitarbeiter(null);
        setSelectedGroup('');
        setFilteredMitarbeiter([]);
    };

    const handleDeleteFromGroup = async () => {
        if (!actionItem) return;

        const updatedGroups = infoGroups.filter((item) => item.id !== actionItem.id && !(item.userId === actionItem.userId && item.groupId === actionItem.groupId));
        setInfoGroups(updatedGroups);
        setIsDeleteDialogOpen(false);
        setActionItem(null);
    };

    const handleSave = async () => {
        if (!dataService) return;
        setIsSaving(true);
        try {
            await dataService.setInfoGroups(infoGroups);
            console.log('Info groups saved successfully');
            setErrorMessage(null);
            setShowSuccessMessage(true);
            setTimeout(() => setShowSuccessMessage(false), 5000);
        } catch (error) {
            console.error('Error saving info groups:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Fehler beim Speichern der Informationsgruppen');
            setTimeout(() => setErrorMessage(null), 5000);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={styles.container}>
            <h3>Information Groups</h3>

            {showSuccessMessage && (
                <MessageBar intent="success">
                    <MessageBarBody>
                        <MessageBarTitle>Erfolg</MessageBarTitle>
                        Informationsgruppen erfolgreich gespeichert
                    </MessageBarBody>
                </MessageBar>
            )}
            {errorMessage && (
                <MessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>Fehler</MessageBarTitle>
                        {errorMessage}
                    </MessageBarBody>
                </MessageBar>
            )}

            <div className={styles.formSection}>
                <Field label="Mitarbeiter" required>
                    <Combobox
                        className={styles.combobox}
                        placeholder="UPN oder Name eingeben..."
                        value={searchQuery}
                        onInput={handleMitarbeiterSearch}
                        onOptionSelect={handleMitarbeiterSelect}
                    >
                        {filteredMitarbeiter.length > 0 ? (
                            filteredMitarbeiter.map((mitarbeiter) => {
                                const displayName = mitarbeiter.fullName || `${mitarbeiter.vorname} ${mitarbeiter.nachname}`;
                                return (
                                    <Option
                                        key={mitarbeiter.personalID}
                                        value={mitarbeiter.personalID.toString()}
                                        text={displayName}
                                    >
                                        {displayName}
                                    </Option>
                                );
                            })
                        ) : (
                            <Option value="" disabled>
                                Keine Mitarbeiter gefunden
                            </Option>
                        )}
                    </Combobox>
                </Field>

                <Field label="Gruppe" required>
                    <Dropdown
                        className={styles.dropdown}
                        placeholder="Gruppe wählen..."
                        value={selectedGroup ? AVAILABLE_GROUPS.find((g) => g.value === selectedGroup)?.text || '' : ''}
                        selectedOptions={selectedGroup ? [selectedGroup] : []}
                        onOptionSelect={(_, data) => {
                            setSelectedGroup(data.optionValue || '');
                        }}
                    >
                        {AVAILABLE_GROUPS.map((group) => (
                            <Option key={group.value} value={group.value} text={group.text}>
                                {group.text}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>

                <Button
                    appearance="primary"
                    icon={<PersonAddRegular />}
                    onClick={handleAddToGroup}
                    disabled={!selectedMitarbeiter || !selectedGroup}
                >
                    Hinzufügen
                </Button>
            </div>

            <div className={styles.formSection} style={{ marginTop: tokens.spacingVerticalL }}>
                <Button
                    appearance="primary"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? 'Wird gespeichert...' : 'Speichern'}
                </Button>
            </div>

            <div className={styles.overviewSection}>
                <Divider />
                <h3 style={{ marginTop: '20px' }}>Übersicht</h3>
                <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
                    {AVAILABLE_GROUPS.map((group) => (
                        <Tab key={group.value} value={group.value}>
                            {group.text} ({getMembersByGroup(group.value).length})
                        </Tab>
                    ))}
                </TabList>

                <div className={styles.tabContent}>
                    {getMembersByGroup(selectedTab).length > 0 ? (
                        getMembersByGroup(selectedTab).map((item, index) => (
                            <div key={`${item.userId}-${item.groupId}-${index}`} className={styles.memberCard}>
                                <div className={styles.memberName}>{item.userName}</div>
                                <Button
                                    icon={<DeleteRegular />}
                                    title="Aus Gruppe entfernen"
                                    appearance="outline"
                                    onClick={() => {
                                        setActionItem(item);
                                        setIsDeleteDialogOpen(true);
                                    }}
                                    className={styles.deleteButton}
                                />
                            </div>
                        ))
                    ) : (
                        <p
                            style={{
                                color: tokens.colorNeutralForeground3,
                                padding: tokens.spacingVerticalM,
                            }}
                        >
                            Keine Mitarbeiter in dieser Gruppe.
                        </p>
                    )}
                </div>
            </div>

            <Dialog
                open={isDeleteDialogOpen}
                onOpenChange={(_e, data) => setIsDeleteDialogOpen(data.open)}
            >
                <DialogSurface>
                    <DialogTitle style={{ marginBottom: '10px' }}>
                        Aus Gruppe entfernen
                    </DialogTitle>
                    <DialogContent>
                        {actionItem && (
                            <span>
                                Möchten Sie <strong>{actionItem.userName}</strong> wirklich aus der Gruppe{' '}
                                <strong>{actionItem.groupName}</strong> entfernen?
                            </span>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            appearance="secondary"
                            onClick={() => setIsDeleteDialogOpen(false)}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            appearance="primary"
                            style={{ backgroundColor: 'firebrick' }}
                            onClick={handleDeleteFromGroup}
                        >
                            Entfernen
                        </Button>
                    </DialogActions>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default InformationGroupsTab;
